import type { ServiceDefinition, ServiceCapability } from "../types/api.js";
import { getStarknetCoordinates } from "../chains.js";

const SN = getStarknetCoordinates("STARKNET");

const SERVICES = {
  "mip-erc721": {
    id: "mip-erc721",
    displayName: "IP Collection",
    description: "Tokenize intellectual property as a per-creator ERC-721 collection.",
    standard: "ERC721",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.collection721!,
        startBlock: SN.collection721StartBlock!,
      },
    },
    uiVariant: "standard",
    capabilities: ["list", "buy", "make_offer", "cancel", "transfer", "mint", "remix", "license"],
    events: [
      { name: "CollectionCreated", emittedBy: "factory" },

    ],
    metadataSchema: { licenseDefault: "CC BY-SA" },
  },
  "ip-erc721": {
    id: "ip-erc721",
    displayName: "Programmable IP",
    description: "One shared ERC-721 contract any wallet can mint into.",
    standard: "ERC721",
    provenance: "MEDIALANE",
    uiVariant: "standard",
    capabilities: ["list", "buy", "make_offer", "cancel", "transfer", "mint", "remix", "license"],

    metadataSchema: { licenseDefault: "CC BY-SA" },
  },
  "data-tokenization-erc721": {
    id: "data-tokenization-erc721",
    displayName: "Data Tokenization",
    description: "Establish verifiable ownership of your data, with licensing terms that hold up wherever it travels.",
    standard: "ERC721",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.dataTokenization721!,
        startBlock: SN.dataTokenization721StartBlock!,
      },
    },
    uiVariant: "standard",
    capabilities: ["list", "buy", "make_offer", "cancel", "transfer", "mint", "remix", "license"],
    events: [
      { name: "CollectionCreated", emittedBy: "factory" },
    ],
    metadataSchema: { licenseDefault: "CC BY-SA" },
  },
  "mip-erc1155": {
    id: "mip-erc1155",
    displayName: "NFT Editions",
    description: "Per-creator ERC-1155 collection; creator mints editions.",
    standard: "ERC1155",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.collection1155!,
        classHash: SN.collection1155ClassHash!,
        startBlock: SN.collection1155StartBlock!,
      },
    },
    uiVariant: "edition",
    capabilities: ["list", "buy", "make_offer", "cancel", "transfer", "mint", "remix", "license"],
    events: [
      { name: "CollectionDeployed", emittedBy: "factory" },
    ],
    metadataSchema: { licenseDefault: "CC BY-SA" },
  },
  "pop-protocol": {
    id: "pop-protocol",
    displayName: "POP Protocol",
    description: "Soulbound proof-of-presence collectibles per event.",
    standard: "ERC721",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.popFactory!,
        classHash: SN.popCollectionClassHash!,
      },
    },
    uiVariant: "pop",
    capabilities: ["claim", "mint"],
    events: [
      { name: "CollectionCreated", emittedBy: "factory" },
      { name: "AllowlistUpdated", emittedBy: "instance", poll: "slow" },
    ],
    metadataSchema: { licenseDefault: "CC BY-SA" },
  },
  "drop-collection": {
    id: "drop-collection",
    displayName: "Collection Drop",
    description: "Sequential mint with claim windows + allowlist.",
    standard: "ERC721",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.dropFactory!,
        classHash: SN.dropCollectionClassHash!,
      },
    },
    uiVariant: "drop",
    capabilities: ["claim", "list", "buy", "make_offer", "cancel", "transfer"],
    events: [
      { name: "DropCreated", emittedBy: "factory" },
      { name: "AllowlistUpdated", emittedBy: "instance", poll: "slow" },
    ],
    metadataSchema: { licenseDefault: "CC BY-SA" },
  },
  "ip-tickets": {
    id: "ip-tickets",
    displayName: "IP Tickets",
    description: "Create verifiable on-chain tickets — mint to attendees, trade like any collection.",
    standard: "ERC1155",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.ipTicketsFactory!,
        classHash: SN.ipTicketCollectionClassHash!,
      },
    },
    uiVariant: "ticket",
    capabilities: ["mint", "redeem", "list", "buy", "make_offer", "cancel", "transfer"],
    events: [
      { name: "CollectionDeployed", emittedBy: "factory" },
      { name: "TicketCreated", emittedBy: "instance" },
    ],
    metadataSchema: { licenseDefault: "CC BY-SA" },
  },
  "ip-ticketing": {
    id: "ip-ticketing",
    displayName: "IP Ticketing",
    description: "Distribute verifiable on-chain tickets to a list of recipients in one run — everyone gets a wallet and their ticket.",
    standard: "ERC1155",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.ipTicketsFactory!,
        classHash: SN.ipTicketCollectionClassHash!,
      },
    },
    uiVariant: "ticket",
    capabilities: ["mint", "airdrop", "redeem", "list", "buy", "make_offer", "cancel", "transfer"],
    events: [
      { name: "CollectionDeployed", emittedBy: "factory" },
      { name: "TicketCreated", emittedBy: "instance" },
    ],
    metadataSchema: { licenseDefault: "CC BY-SA" },
  },
  "ip-club": {
    id: "ip-club",
    displayName: "IP Club",
    description: "Membership clubs — create tiers, mint membership cards, trade like any collection.",
    standard: "ERC1155",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.ipClubFactory!,
        classHash: SN.ipClubCollectionClassHash!,
      },
    },
    uiVariant: "club",
    capabilities: ["mint", "list", "buy", "make_offer", "cancel", "transfer"],
    events: [
      { name: "ClubDeployed", emittedBy: "factory" },
      { name: "MembershipCreated", emittedBy: "instance" },
    ],
    metadataSchema: { licenseDefault: "CC BY-SA" },
  },
  "ip-sponsorship": {
    id: "ip-sponsorship",
    displayName: "IP Sponsorship",
    description: "Sponsorship offers and licenses anchored to an existing Medialane asset.",
    standard: "ERC721",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.ipSponsorship!,
        classHash: SN.ipSponsorshipClassHash!,
        startBlock: SN.ipSponsorshipStartBlock!,
      },
    },
    uiVariant: "standard",

    capabilities: ["sponsor", "transfer"],
    events: [
      { name: "OfferCreated", emittedBy: "factory" },
      { name: "ProposalCreated", emittedBy: "factory" },
      { name: "LicenseMinted", emittedBy: "factory" },
    ],
    metadataSchema: { licenseDefault: "CC BY-SA" },
  },
  "creator-coin": {
    id: "creator-coin",
    displayName: "Creator Coin",
    description:
      "Launch a fixed-supply social token with permanently-locked Ekubo liquidity.",
    standard: "ERC20",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.creatorCoinFactory!,
        classHash: SN.creatorCoinFactoryClassHash!,
        startBlock: SN.creatorCoinStartBlock!,
      },
    },
    uiVariant: "coin",

    capabilities: ["launch", "swap", "transfer"],
    events: [
      { name: "CreatorCoinCreated", emittedBy: "factory" },
      { name: "CreatorCoinLaunched", emittedBy: "factory" },
    ],
  },
  "medialane-marketplace-erc721": {
    id: "medialane-marketplace-erc721",
    displayName: "Medialane Marketplace (ERC-721)",
    description: "ERC-721 order matching venue.",
    standard: "ERC721",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.marketplace721!,
        classHash: SN.marketplace721ClassHash!,
        startBlock: SN.marketplace721StartBlock!,
      },
    },
    uiVariant: "standard",
    capabilities: ["list", "buy", "make_offer", "cancel"],
    events: [
      { name: "OrderCreated", emittedBy: "factory" },
      { name: "OrderFulfilled", emittedBy: "factory" },
      { name: "OrderCancelled", emittedBy: "factory" },
    ],
  },
  "medialane-marketplace-erc1155": {
    id: "medialane-marketplace-erc1155",
    displayName: "Medialane Marketplace (ERC-1155)",
    description: "ERC-1155 order matching venue.",
    standard: "ERC1155",
    provenance: "MEDIALANE",
    onchain: {
      STARKNET: {
        factoryAddress: SN.marketplace1155!,
        classHash: SN.marketplace1155ClassHash!,
        startBlock: SN.marketplace1155StartBlock!,
      },
    },
    uiVariant: "edition",
    capabilities: ["list", "buy", "make_offer", "cancel"],
    events: [
      { name: "OrderCreated", emittedBy: "factory" },
      { name: "OrderFulfilled", emittedBy: "factory" },
      { name: "OrderCancelled", emittedBy: "factory" },
    ],
  },
  "unruggable-erc20": {
    id: "unruggable-erc20",
    displayName: "Unruggable",
    description:
      "An ERC-20 launched through the unruggable.meme protocol, the same contract Medialane's Creator Coin is forked from. Not deployed via a Medialane service, but exposes the same on-chain guarantees: capped team allocation, permanently locked liquidity, fixed supply. Brought in by owner claim or admin/partnership — never bulk-indexed.",
    standard: "ERC20",
    provenance: "EXTERNAL",
    uiVariant: "coin",
    capabilities: ["swap", "transfer"],
  },
  "external-erc20": {
    id: "external-erc20",
    displayName: "Memecoin",
    description:
      "A plain ERC-20 not deployed via a Medialane service and carrying no protocol-level guarantees. Brought in by owner claim or admin/partnership — never bulk-indexed. Generalizes to future chains.",
    standard: "ERC20",
    provenance: "EXTERNAL",
    uiVariant: "coin",
    capabilities: ["swap", "transfer"],
  },
  "external-erc721": {
    id: "external-erc721",
    displayName: "External ERC-721",
    description: "ERC-721 contract not deployed via a Medialane service.",
    standard: "ERC721",
    provenance: "EXTERNAL",
    uiVariant: "standard",
    capabilities: ["list", "buy", "make_offer", "cancel", "transfer"],
  },
  "external-erc1155": {
    id: "external-erc1155",
    displayName: "External ERC-1155",
    description: "ERC-1155 contract not deployed via a Medialane service.",
    standard: "ERC1155",
    provenance: "EXTERNAL",
    uiVariant: "edition",
    capabilities: ["list", "buy", "make_offer", "cancel", "transfer"],
  },
} as const satisfies Record<string, ServiceDefinition>;

export type ServiceId = keyof typeof SERVICES;

export function isServiceId(id: string | null | undefined): id is ServiceId {
  return typeof id === "string" && id in SERVICES;
}

export function getService(id: string | null | undefined): ServiceDefinition | undefined {
  return id && id in SERVICES ? (SERVICES as Record<string, ServiceDefinition>)[id] : undefined;
}

export function listServices(): ServiceDefinition[] {
  return Object.values(SERVICES);
}

export function getServicesByCapability(cap: ServiceCapability): ServiceDefinition[] {
  return Object.values(SERVICES).filter(
    (s) => (s.capabilities as readonly ServiceCapability[]).includes(cap),
  );
}

export function hasCapability(
  id: string | null | undefined,
  cap: ServiceCapability,
): boolean {
  return getService(id)?.capabilities.includes(cap) ?? false;
}
